import React from "react";
import { useTenant } from "../context/TenantContext";
import "../styles/shipping.css";

const Shipping = () => {
  const { tenant } = useTenant();

  // Helper function to format phone number for tel: links (same as Contact page)
  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  // Get shipping content from tenant (loaded from backend via ShippingContent field)
  const shippingContent = tenant?.ShippingContent;

  // Check if we have custom content from the admin
  const hasCustomContent = shippingContent && shippingContent.trim() !== "";

  // If custom content exists, render it directly
  if (hasCustomContent) {
    return (
      <div className="shipping-page">
        <div className="container">
          <section className="shipping-hero">
            <h1>Shipping Information</h1>
            <p>Everything you need to know about our shipping policy</p>
          </section>
          <div
            className="shipping-content"
            dangerouslySetInnerHTML={{ __html: shippingContent }}
          />
        </div>
      </div>
    );
  }

  // Default content using tenant data from backend (same approach as Contact page)
  return (
    <div className="shipping-page">
      <div className="container">
        <section className="shipping-hero">
          <h1>Shipping Information</h1>
          <p>Everything you need to know about our shipping policy</p>
        </section>

        <div className="shipping-content">
          <div className="shipping-section">
            <h2>Shipping Information</h2>
            <p>
              We strive to deliver your orders quickly and efficiently. Below is
              our shipping policy for orders placed with{" "}
              {tenant?.BusinessName || "us"}.
            </p>
          </div>

          <div className="shipping-section">
            <h3>Shipping Rates</h3>
            <ul>
              <li>
                <strong>Standard Shipping:</strong> R99
                {tenant?.City && ` (${tenant.City} area)`}
              </li>
              <li>
                <strong>Free Shipping:</strong> On orders over R500
              </li>
              <li>
                <strong>Express Shipping:</strong> R149
              </li>
              {tenant?.City && (
                <li>
                  <strong>Local Pickup:</strong> Free (available in{" "}
                  {tenant.City})
                </li>
              )}
            </ul>
          </div>

          <div className="shipping-section">
            <h3>Delivery Times</h3>
            <ul>
              <li>
                <strong>Main Centers:</strong> 2-3 business days
              </li>
              <li>
                <strong>Regional Areas:</strong> 3-5 business days
              </li>
              <li>
                <strong>Rural Areas:</strong> 5-7 business days
              </li>
              {tenant?.Country && (
                <li>
                  <strong>International:</strong> Please contact us for
                  international shipping rates to {tenant.Country}
                </li>
              )}
            </ul>
          </div>

          <div className="shipping-section">
            <h3>Order Processing</h3>
            <ul>
              <li>Orders placed before 2pm ship same day</li>
              <li>Orders after 2pm ship next business day</li>
              <li>Weekend orders ship on Monday</li>
              <li>You will receive a tracking number via email</li>
            </ul>
          </div>

          <div className="shipping-section">
            <h3>Shipping Address</h3>
            <p>
              Please ensure your shipping address is correct. We are not
              responsible for orders delivered to incorrect addresses provided
              by the customer.
            </p>
            {tenant && (tenant.AddressLine1 || tenant.City) && (
              <p>
                <strong>Store Location:</strong>{" "}
                {[
                  tenant?.AddressLine1,
                  tenant?.AddressLine2,
                  tenant?.City,
                  tenant?.State,
                  tenant?.PostalCode,
                  tenant?.Country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
          </div>

          <div className="shipping-section">
            <h3>Contact Information</h3>
            <p>If you have any questions about shipping, please contact us:</p>

            {/* Email - from backend */}
            {tenant?.BusinessEmail && (
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              </p>
            )}

            {/* Phone - from backend with proper formatting */}
            {tenant?.BusinessPhone && (
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                  {tenant.BusinessPhone}
                </a>
              </p>
            )}

            {/* Fallback contact info if no tenant data exists */}
            {!tenant?.BusinessEmail &&
              !tenant?.BusinessPhone && (
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:shipping@example.com">shipping@example.com</a>
                </p>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shipping;
